import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";

const DELAI = 10000;

// useLayoutEffect dans le navigateur, useEffect au pré-rendu (sans DOM).
const useEffetAvantPeinture = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Carrousel accessible (WCAG 2.2.2). Les diapositives sont superposées ;
 * la diapositive marque son texte (.diapo-texte) et son image
 * (.diapo-image) pour que index.css enchaîne les deux sans écran vide. Onglets nommés et commandes
 * en bas, glissement du doigt sur écran tactile, lecture automatique qu'un
 * bouton met en pause. La lecture s'arrête au survol et au focus ; si la
 * personne a demandé à réduire les animations, rien ne bouge ni ne défile
 * seul. Les diapositives cachées sont inertes.
 *
 * `rendu(diapositive, index)` dessine une diapositive ; `nom(diapositive)`
 * donne son libellé (onglet et nom accessible).
 */
export const Carrousel = ({ diapositives, libelle, rendu, nom, automatique = true }) => {
  const [index, setIndex] = useState(0);
  const [lecture, setLecture] = useState(false);
  const [suspendu, setSuspendu] = useState(false);
  const [visible, setVisible] = useState(true);
  const racine = useRef(null);
  const depart = useRef(null);
  const diapos = useRef([]);
  const [hauteur, setHauteur] = useState(null);
  const total = diapositives.length;

  // La scène prend la hauteur de la diapositive affichée (et non de la plus
  // haute) : pas de vide sous une diapositive courte, surtout sur téléphone.
  // Avant JavaScript, la grille garde la hauteur de la plus haute.
  useEffetAvantPeinture(() => {
    const el = diapos.current[index];
    if (!el) return undefined;
    const mesurer = () => setHauteur(el.offsetHeight);
    mesurer();
    const obs = new ResizeObserver(mesurer);
    obs.observe(el);
    return () => obs.disconnect();
  }, [index]);

  useEffect(() => {
    const reduit = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setLecture(automatique && !reduit);
  }, [automatique]);

  const aller = useCallback((cible) => setIndex((cible + total) % total), [total]);

  // Hors de l'écran, la lecture s'arrête : un changement de hauteur ne doit
  // pas faire bouger le texte que la personne lit plus bas.
  useEffect(() => {
    const el = racine.current;
    if (!el || !("IntersectionObserver" in window)) return undefined;
    const obs = new IntersectionObserver(([e]) => setVisible(e.intersectionRatio >= 0.5), {
      threshold: [0, 0.5, 1],
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!lecture || suspendu || !visible) return undefined;
    const minuterie = setTimeout(() => aller(index + 1), DELAI);
    return () => clearTimeout(minuterie);
  }, [lecture, suspendu, visible, index, aller]);

  // Glissement du doigt : au moins 50 px, plus horizontal que vertical.
  const surDebutToucher = (e) => {
    const t = e.touches[0];
    depart.current = { x: t.clientX, y: t.clientY };
  };
  const surFinToucher = (e) => {
    if (!depart.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - depart.current.x;
    const dy = t.clientY - depart.current.y;
    depart.current = null;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) aller(index + (dx < 0 ? 1 : -1));
  };

  const bouton =
    "flex h-11 w-11 flex-none items-center justify-center rounded-full border border-border bg-white text-brand";

  return (
    <section
      ref={racine}
      aria-roledescription="carrousel"
      aria-label={libelle}
      onMouseEnter={() => setSuspendu(true)}
      onMouseLeave={() => setSuspendu(false)}
      onFocus={() => setSuspendu(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setSuspendu(false);
      }}
    >
      <div
        onTouchStart={surDebutToucher}
        onTouchEnd={surFinToucher}
        aria-live={lecture && !suspendu ? "off" : "polite"}
        className="carrousel-scene"
        style={hauteur ? { height: hauteur } : undefined}
      >
        {diapositives.map((d, i) => (
          <div
            key={nom(d)}
            ref={(el) => (diapos.current[i] = el)}
            role="group"
            aria-roledescription="diapositive"
            aria-label={`${i + 1} sur ${total} : ${nom(d)}`}
            inert={i !== index ? true : undefined}
            data-etat={i === index ? "active" : "cachee"}
            className="carrousel-diapo"
          >
            {rendu(d, i)}
          </div>
        ))}
      </div>

      {/* Barre du bas : onglets nommés à gauche, commandes à droite. */}
      <div className="mt-6 flex flex-col items-center gap-4 sm:mt-8 lg:flex-row lg:justify-between">
        <div className="hidden flex-wrap justify-center gap-2 sm:flex lg:justify-start">
          {diapositives.map((d, i) => (
            <button
              key={nom(d)}
              type="button"
              onClick={() => aller(i)}
              aria-current={i === index ? "true" : undefined}
              className={`rounded-full border px-4 py-2 text-vt-controle font-bold ${
                i === index
                  ? "border-brand bg-brand text-white"
                  : "border-border bg-white text-brand"
              }`}
            >
              {nom(d)}
            </button>
          ))}
        </div>

        <div className="flex flex-none items-center gap-3">
          {automatique && (
            <button
              type="button"
              onClick={() => setLecture((l) => !l)}
              aria-label={lecture ? "Mettre le carrousel en pause" : "Relancer le carrousel"}
              title={lecture ? "Mettre en pause" : "Relancer"}
              className={bouton}
            >
              {lecture ? (
                <Pause className="h-5 w-5" strokeWidth={2.2} aria-hidden="true" />
              ) : (
                <Play className="h-5 w-5" strokeWidth={2.2} aria-hidden="true" />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => aller(index - 1)}
            aria-label="Diapositive précédente"
            title="Précédente"
            className={bouton}
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={2.4} aria-hidden="true" />
          </button>
          <span className="min-w-[3.5rem] text-center text-vt-controle font-bold text-t2">
            {index + 1} / {total}
          </span>
          <button
            type="button"
            onClick={() => aller(index + 1)}
            aria-label="Diapositive suivante"
            title="Suivante"
            className={bouton}
          >
            <ChevronRight className="h-5 w-5" strokeWidth={2.4} aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
};
